import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const FORM_URL = "https://readdy.ai/api/form/d6vbfg6dp0k8rarn7uag";

export default function ContactSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setStatus("loading");
    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => params.append(key, value.toString()));
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) { setStatus("success"); form.reset(); setCharCount(0); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  return (
    <section id="contact" className="py-28 bg-[#F7F5F2]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#C9A96E] text-sm font-semibold tracking-widest uppercase">{t("ct.eyebrow")}</span>
          <h2 className="text-4xl xl:text-5xl font-bold text-[#0C0C0C] mt-3 mb-5 leading-tight">{t("ct.title")}</h2>
          <p className="text-[#0C0C0C]/50 text-lg max-w-xl mx-auto leading-relaxed">{t("ct.sub")}</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-10">
          <div className="xl:col-span-2 flex flex-col gap-6">
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8953A]/10 flex-shrink-0">
                <i className="ri-mail-line text-[#C8953A] text-xl" />
              </div>
              <div>
                <div className="text-[#0C0C0C]/40 text-xs font-medium uppercase tracking-wider mb-1">{t("ct.info1.label")}</div>
                <div className="text-[#0C0C0C] font-medium text-sm">{t("ct.info1.value")}</div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8953A]/10 flex-shrink-0">
                <i className="ri-time-line text-[#C8953A] text-xl" />
              </div>
              <div>
                <div className="text-[#0C0C0C]/40 text-xs font-medium uppercase tracking-wider mb-1">{t("ct.info3.label")}</div>
                <div className="text-[#0C0C0C] font-medium text-sm">{t("ct.info3.value")}</div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-3 bg-white rounded-3xl p-8 md:p-10">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mb-4">
                  <i className="ri-check-line text-green-500 text-3xl" />
                </div>
                <h3 className="text-[#0C0C0C] text-xl font-bold mb-2">{t("ct.form.successTitle")}</h3>
                <p className="text-[#0C0C0C]/50 text-sm">{t("ct.form.successDesc")}</p>
                <button
                  onClick={() => { setStatus("idle"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="whitespace-nowrap mt-6 text-[#C9A96E] text-sm font-medium cursor-pointer hover:underline"
                >
                  {t("ct.form.refill")}
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[#0C0C0C]/50 text-xs font-medium mb-2 uppercase tracking-wider">{t("ct.form.nameLabel")}</label>
                    <input name="name" type="text" required placeholder={t("ct.form.namePlaceholder")} className="w-full bg-[#F7F5F2] border border-transparent focus:border-[#C8953A] rounded-xl px-4 py-3 text-sm text-[#0C0C0C] outline-none transition-colors duration-200" />
                  </div>
                  <div>
                    <label className="block text-[#0C0C0C]/50 text-xs font-medium mb-2 uppercase tracking-wider">{t("ct.form.emailLabel")}</label>
                    <input name="email" type="email" required placeholder={t("ct.form.emailPlaceholder")} className="w-full bg-[#F7F5F2] border border-transparent focus:border-[#C8953A] rounded-xl px-4 py-3 text-sm text-[#0C0C0C] outline-none transition-colors duration-200" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#0C0C0C]/50 text-xs font-medium mb-2 uppercase tracking-wider">{t("ct.form.messageLabel")}</label>
                  <textarea name="message" rows={5} placeholder={t("ct.form.msgPlaceholder")} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} className="w-full bg-[#F7F5F2] border border-transparent focus:border-[#C8953A] rounded-xl px-4 py-3 text-sm text-[#0C0C0C] outline-none transition-colors duration-200 resize-none" />
                  <div className="text-right text-xs text-[#0C0C0C]/30 mt-1">{charCount} / 500</div>
                </div>
                {status === "error" && <p className="text-red-500 text-sm">{t("ct.form.error")}</p>}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="whitespace-nowrap w-full bg-[#0C0C0C] hover:bg-[#C9A96E] text-white hover:text-[#0C0C0C] font-semibold py-4 rounded-xl text-sm transition-all duration-300 cursor-pointer flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? t("ct.form.submitting") : t("ct.form.submit")}
                  <i className={`${status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} ml-2`} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
